/* eslint-disable max-lines */
import { inject, observer } from 'mobx-react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

import { AnalyticsStore, ContextStore, ExperimentStore, mergeQueryString, PageStore, QueryRoute } from 'bernie-core';
import { ErrorBoundary, ViewLarge, Viewport, ViewSmall } from 'bernie-view';
import { withLocalization } from 'bernie-l10n';

import { CustomerNotificationLocation } from '@shared-ui/customer-notifications';
import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { ILocalization } from '@shared-ui/localization-context';
import { Qualtrics } from '@shared-ui/customer-qualtrics';
import { VirtualAgentControl } from '@shared-ui/customer-virtual-agent-control';

import { MultiItemStore } from 'stores/multi-item-store';
import { UIPageStore } from 'stores/ui-page-store';
import { VirtualAgentControlStore } from 'stores/virtual-agent-control-store';

import { MitStep } from 'models/multi-item/multi-item-types';
import { SearchPageName } from 'constants/pages';

import { classNames, Level, liveAnnounce } from 'uitk-react-utils';
import { UitkEmptyState, UitkEmptyStateActionArea } from 'uitk-react-empty-state';
import { UitkHeading } from 'uitk-react-text';
import { UitkIcon } from 'uitk-react-icons';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkTertiaryButton } from 'uitk-react-button';

import { handleRetry } from 'utils/error-handling-utils';

import { CustomerNotifications } from 'components/common/customer-notifications';
import { DelayedSortAndFilterFetchExperiment } from 'components/search/experiments/delayed-sort-and-filter-experiment';
import { ExitIntent } from 'components/search/exit-intent';
import { LoyaltyEducationDialog } from 'components/common/loyalty-education/loyalty-education-dialog';
import { MainRegion } from 'components/layout/pages/main-region';
import { MasterPage, MasterPageRoute } from 'components/layout/pages/master-page';
import { MesoSideRail } from 'components/meso/meso-side-rail';
import { MitHeader } from 'components/multi-item/multi-item-header';
import { MultiItem } from 'components/multi-item';
import { PackageError } from 'components/multi-item/package-error';
import { Playback } from 'components/search/playback/playback';
import { Results } from 'components/search/results/results';
import { ResultsHeader } from 'components/search/results-header';
import { ShortlistProvider } from 'components/common/shortlist/shortlist-provider';
import { Sidebar } from 'components/search/sidebar';
import { SideRailRegion } from 'components/layout/pages/side-rail-region';
import { ToolbarRegion } from 'components/layout/pages/toolbar-region';
import * as ViewportListener from 'components/common/lazy/viewport-listener.js';

interface SearchProps extends RouteComponentProps {
  advertising?: any;
  analytics?: AnalyticsStore;
  context?: ContextStore;
  experiment?: ExperimentStore;
  l10n?: ILocalization;
  multiItem?: MultiItemStore;
  page?: PageStore;
  route?: MasterPageRoute;
  searchResults?: any;
  uiPage?: UIPageStore;
  virtualAgentControlStore?: VirtualAgentControlStore;
}

interface SearchState {
  jsEnabled: boolean;
  lastLoadingStateMessage: any;
}

interface LargeSearchResultsProps {
  analytics?: any;
  jsEnabled: boolean;
  results: JSX.Element;
  showTools: boolean;
}

const MasterPageToolbarTypes = MasterPage.wrappedComponent.toolbarTypes;
const MasterPageSideRailTypes = MasterPage.wrappedComponent.sideRailTypes;

const RENDER_REMAINING_AFTER_INDEX = 10;

@withRouter
@withLocalization
@inject('advertising', 'analytics', 'context', 'experiment', 'multiItem', 'page', 'searchResults', 'uiPage', 'virtualAgentControlStore')
@observer
export default class Search extends React.Component<SearchProps, SearchState> {
  resultRefs = [];
  fetchOnScrollCalled = false;

  constructor(props) {
    super(props);

    this.handleOnDismiss = this.handleOnDismiss.bind(this);
    this.handleShowMore = this.handleShowMore.bind(this);
    this.markUsable = this.markUsable.bind(this);
    this.setResultRef = this.setResultRef.bind(this);
    this.fetchOnScroll = this.fetchOnScroll.bind(this);

    this.state = {
      jsEnabled: false,
      lastLoadingStateMessage: '',
    };

    if (
      this.props.searchResults &&
      this.props.searchResults.searchCriteria &&
      this.props.searchResults.searchCriteria.multiProp === 'true'
    ) {
      this.props.multiItem && this.props.multiItem.getOrCreateCart(this.props.context);
    }
    this.props.advertising?.includeAd();
    if (this.props.multiItem?.multiItemContext) {
      this.props.advertising?.trigger(SearchPageName[this.props.multiItem.multiItemContext.packageType]);
    } else {
      this.props.advertising?.trigger('page.Hotels.Search');
    }
  }

  get backToChangeSearchQuery() {
    const { location } = this.props;

    return mergeQueryString(location.search, { pwaOverlay: 'changeSearch' });
  }

  get loading() {
    const { searchResults, uiPage } = this.props;

    return searchResults.isLoading && !uiPage.displayProgressIndicator;
  }

  markUsable(usable, pageUsableMarked) {
    const { analytics, searchResults } = this.props;

    if (usable && !pageUsableMarked) {
      analytics.markPageUsable();

      searchResults.pageUsableMarked = true;
    }
  }

  componentDidMount() {
    const { usable, pageUsableMarked } = this.props.searchResults;

    this.markUsable(usable, pageUsableMarked);

    this.announceLoadingState();
    this.trackDisambiguationSearch();
    this.trackSearchResultsError();

    this.setState({ jsEnabled: true });
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { usable, pageUsableMarked } = nextProps.searchResults;

    // This if statement exists to get around a unit test case for re-rendering.
    if (this.markUsable) {
      this.markUsable(usable, pageUsableMarked);
    }

    // This happens once in componentDidMount and should not require the entire component tree to rerender
    const jsEnabledChanged = this.state.jsEnabled !== nextState.jsEnabled;

    // This should be fixed in the LocationReactor component in bernie
    const locationSetForTheFirstTime = this.props.location === undefined && typeof nextProps?.location === 'object';

    // If either of the conditions are true then we will prevent the rerender, setting shouldRerender to false
    const shouldRerender = !(jsEnabledChanged || locationSetForTheFirstTime);

    return shouldRerender;
  }

  trackDisambiguationSearch() {
    if (this.props.multiItem && this.props.multiItem.isDisambigSearch()) {
      if (this.props.analytics && typeof this.props.analytics.trackEvent === 'function') {
        const events = [{ name: 'SearchResultsDisambig', error: false }];
        this.props.analytics.trackEvent(this.props.multiItem.referrer, 'package disambig search', null, events);
      }
    }
  }

  trackSearchResultsError() {
    const { isEmpty, isError } = this.props.searchResults;

    if (isEmpty || isError) {
      if (this.props.analytics && typeof this.props.analytics.trackEvent === 'function') {
        this.props.analytics.trackEvent('SorryMessage', 'SorryMessage', null);
      }
    }
  }

  componentDidUpdate() {
    this.announceLoadingState();
  }

  announceLoadingState() {
    const { isLoading, isError, isEmpty } = this.props.searchResults;
    const { l10n } = this.props;
    const accessibilityLabel =
      this.props.searchResults?.sortAndFilter?.options?.accessibilityLabel ??
      l10n.formatText('hotels.searchResults.resultsUpdated.liveAnnounce');

    let message;
    if (isLoading) {
      message = l10n.formatText('hotels.searchResults.resultsLoading.liveAnnounce');
    } else if (isError) {
      message = l10n.formatText('hotels.searchResults.errorRetrievingResults.liveAnnounce');
    } else if (isEmpty) {
      message = l10n.formatText('hotels.searchResults.emptyResults.liveAnnounce');
    } else {
      message = accessibilityLabel;
    }
    if (message !== this.state.lastLoadingStateMessage) {
      liveAnnounce(message, Level.POLITE);
      this.setState({ lastLoadingStateMessage: message });
    }
  }

  handleOnDismiss() {
    const { history } = this.props;
    history.goBack();
  }

  setResultRef(ref, index) {
    if (ref === null || this.resultRefs[index] === ref) {
      return;
    }

    this.resultRefs[index] = ref;

    if (index === RENDER_REMAINING_AFTER_INDEX) {
      ViewportListener.subscribe(
        'window',
        ref,
        {
          didEnter: () => this.props.searchResults.setHotelsMaxDisplayToTotalRenderCount(),
          isAbove: () => this.props.searchResults.setHotelsMaxDisplayToTotalRenderCount(),
        },
        { extendBoundingBoxToBottomOfPage: true, triggerOnce: true }
      );
    }
  }

  render() {
    const { context, experiment, analytics, page, l10n, searchResults, multiItem, virtualAgentControlStore } = this.props;

    const searchCriteria = searchResults?.searchCriteria;
    const { regionId, startDate, endDate } = searchCriteria || {};

    const { legalDisclaimer, loyaltyEducation } = searchResults || {};
    const isMixMatchCart = multiItem && multiItem.cartInfo && multiItem.cartInfo.identifier === 'mixAndMatch';
    const masterPageClasses = classNames('search-results');
    const pageName = page?.pageName || SearchPageName.SA;

    if (isMixMatchCart && multiItem.itemCount > 0) {
      multiItem.emptyCart(context);
    }

    const results = (
      <Results
        handleShowMore={this.handleShowMore}
        legalDisclaimer={legalDisclaimer}
        pageName={pageName}
        setResultRef={this.setResultRef}
        errorMessage={this.renderErrorMessage()}
      />
    );

    return (
      <ShortlistProvider>
        <MasterPage
          className={masterPageClasses}
          route={this.props.route}
          searchCriteria={searchResults.searchCriteria}
          navigationTabs={searchResults.navigationTabs}
          sideRailType={MasterPageSideRailTypes.NEXT_TO_MAIN_AND_TOOLBAR}
          toolbarType={MasterPageToolbarTypes.NORMAL}
          pageHeader={searchResults.resultsHeading}
        >
          <ToolbarRegion className="search-results-toolbar">
            <Playback
              bookingNextProperty={multiItem && multiItem.isTouchedCart && !multiItem.isPackageShoppingPath()}
              shouldShowMultiItemWizardFields={multiItem && multiItem.isPackageShoppingPath()}
              packageType={multiItem && multiItem.getPackageType()}
            />
          </ToolbarRegion>
          <MainRegion className="search-results__main s-x-margin-two">
            <MitHeader {...{ l10n, multiItem, currentStepName: MitStep.HOTEL, page, context, experiment }} />
            <CustomerNotifications
              location={CustomerNotificationLocation.AFTER_HEADER}
              regionId={regionId}
              startDate={startDate}
              endDate={endDate}
            />
            <UitkSpacing padding={{ small: { block: 'three' }, medium: { blockstart: 'one' }, large: { blockstart: 'three' } }}>
              <div className="search-results-listing">
                <Viewport>
                  <ViewSmall>
                    <>
                      <ResultsHeader analytics={analytics} />
                      {results}
                    </>
                  </ViewSmall>
                  <ViewLarge>
                    <LargeSearchResults jsEnabled={this.state.jsEnabled} results={results} showTools={true} />
                  </ViewLarge>
                </Viewport>
              </div>
            </UitkSpacing>

            <CustomerNotifications location={CustomerNotificationLocation.FOOTER} regionId={regionId} />

            <QueryRoute query={{ pwaDialog: 'loyaltyEducation' }}>
              <LoyaltyEducationDialog loyaltyEducation={loyaltyEducation} />
            </QueryRoute>
            {searchResults.exitIntent && multiItem && !multiItem.isPackageShoppingPath() && (
              <ErrorBoundary label="hotel.search.exitIntent">
                <ExitIntent showOnce={true} exitIntent={searchResults.exitIntent} />
              </ErrorBoundary>
            )}
            {multiItem && !multiItem.isPackageShoppingPath() && <MultiItem analytics={analytics} pageName={pageName} />}
            {virtualAgentControlStore.shouldInjectChatbot(context, page, experiment) && (
              <VirtualAgentControl
                applicationName="shopping-pwa"
                pageName={pageName}
                clientOverrides={{
                  subscribedEvents: [virtualAgentControlStore.VAC_WIDGET_OPENED_EVENT, virtualAgentControlStore.VAC_WIDGET_CLOSED_EVENT],
                  eventHandler: event => virtualAgentControlStore.eventHandler(analytics, event),
                }}
                inputs={{}}
              />
            )}
            <DelayedSortAndFilterFetchExperiment />
          </MainRegion>
          <SideRailRegion>
            <MesoSideRail />
          </SideRailRegion>
        </MasterPage>
        <Experiment name="Lodging_PWA_Qualtrics">
          <ExperimentControl />
          <ExperimentVariant bucket={1}>
            <Qualtrics />
          </ExperimentVariant>
        </Experiment>
      </ShortlistProvider>
    );
  }

  refreshAds() {
    if (document && CustomEvent && typeof document.dispatchEvent === 'function') {
      document.dispatchEvent(new CustomEvent('mesoTriggeredRefresh', { detail: { type: 'impressionRefresh' } }));
    }
  }

  showMoreHotels() {
    const { analytics, context, multiItem, searchResults, experiment } = this.props;
    this.props.uiPage.showProgressIndicator(400);

    searchResults.showMoreHotels(analytics, context, multiItem, experiment).then(previousMax => {
      // Move focus to the first newly revealed HSR cards, for better screen reader experience.
      this.resultRefs[previousMax].focus();
    });
  }

  fetchOnScroll() {
    if (!this.fetchOnScrollCalled) {
      const { analytics, context, multiItem, searchResults, experiment } = this.props;
      this.fetchOnScrollCalled = true;
      searchResults.showMoreHotels(analytics, context, multiItem, experiment);
    }
  }

  handleShowMore() {
    this.refreshAds();
    this.showMoreHotels();
  }

  renderErrorMessage() {
    const searchResults = this.props.searchResults || {};
    const { isError, isEmpty, searchCriteria } = searchResults;
    const { l10n, analytics, history, location } = this.props;

    const multiItem = searchCriteria && searchCriteria.misId;

    if (isError || isEmpty) {
      if (multiItem) {
        return (
          <UitkSpacing padding={{ blockstart: 'six', blockend: 'twelve' }}>
            <div className="uitk-type-center">
              <PackageError {...this.props} />
            </div>
          </UitkSpacing>
        );
      }
      const errorMessage =
        !isError && isEmpty
          ? l10n.formatText('hotels.searchResults.emptyResults.text')
          : l10n.formatText('hotels.searchResults.error.text');
      const errorCta = isError ? l10n.formatText('hotels.searchResults.error.cta') : '';

      return (
        <UitkSpacing padding={{ blockend: 'twelve' }}>
          <div className="uitk-type-center" data-stid="empty-state-error">
            <UitkSpacing padding={{ blockstart: 'three' }}>
              <UitkEmptyState data-stid={'retry-search'}>
                <UitkIcon name={'error'} />
                <UitkHeading tag="h2">{errorMessage}</UitkHeading>
                <UitkEmptyStateActionArea>
                  <UitkTertiaryButton
                    name="textonly"
                    onClick={() => handleRetry(':errorOnSearch', 'Try Again on error', analytics, searchResults, history, location)}
                    type="button"
                  >
                    {errorCta}
                  </UitkTertiaryButton>
                </UitkEmptyStateActionArea>
              </UitkEmptyState>
            </UitkSpacing>
          </div>
        </UitkSpacing>
      );
    }

    return null;
  }
}

const LargeSearchResults = ({ analytics, jsEnabled, results, showTools }: LargeSearchResultsProps) => (
  <UitkLayoutGrid columns={4} space="six">
    <UitkLayoutGridItem>
      <div>
        <Sidebar isActive={jsEnabled} />
      </div>
    </UitkLayoutGridItem>
    <UitkLayoutGridItem colSpan={3}>
      <div>
        {showTools && <ResultsHeader analytics={analytics} />}
        {results}
      </div>
    </UitkLayoutGridItem>
  </UitkLayoutGrid>
);
